import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3 } from 'lucide-react';
import PainelServicos from '../relatorios/PainelServicos';
import PainelEmbrioes from '../relatorios/PainelEmbrioes';
import PainelPrenhez from '../relatorios/PainelPrenhez';
import PainelSaidas from '../relatorios/PainelSaidas';
import PainelSemen from '../relatorios/PainelSemen';
import PainelFinanceiro from '../relatorios/PainelFinanceiro';
import { listarSaidas } from '../../../data/estoque';
import { listarSemenDoRelatorio } from '../../../data/semen';
import {
  listarServicos, listarProducaoPorDoadora, listarPrenhez, listarReceptoras,
  aplicarFiltrosLocais,
} from '../../../data/relatorios';

/**
 * Relatórios.
 *
 * Três painéis, cada um com a SUA métrica principal: serviços lideram por
 * faturamento, embriões por produção, prenhez por taxa. Antes era um painel só,
 * e embrião servia de métrica principal até para pergunta de dinheiro.
 *
 * A barra de filtros é compartilhada e fica acima das abas: período e cliente
 * valem para os três, e repetir o filtro em cada aba faria reconfigurar a mesma
 * coisa três vezes.
 */

const hoje = () => new Date().toISOString().slice(0, 10);
const mesesAtras = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

const ABAS = [
  { id: 'servicos', rotulo: 'Serviços' },
  { id: 'embrioes', rotulo: 'Embriões' },
  { id: 'prenhez',  rotulo: 'Prenhez' },
  { id: 'saidas',   rotulo: 'Saídas' },
  { id: 'semen',    rotulo: 'Sêmen' },
  /*
   * O dinheiro é o único relatório RESTRITO.
   *
   * Produção, prenhez e estoque são o trabalho de todos, e quem lança precisa
   * poder conferir o que lançou. Faturamento e saldo por cliente são de quem
   * responde pelo contrato — `podeVerDinheiro` é o `canEdit` do sistema, que
   * é admin ou moderador.
   *
   * Os números vêm do Resumo do Período do Dashboard, que era visível a
   * qualquer pessoa logada, e do topo da aba Financeiro. Não é um dado novo
   * exposto: é o mesmo dado, num lugar com porta.
   */
  { id: 'financeiro', rotulo: 'Financeiro', restrito: true },
];

// Cada painel agrupa pelo que faz sentido nele. Oferecer "doadora" no relatório
// de serviços seria oferecer uma dimensão que aquele conjunto não tem.
const DIMENSOES = {
  servicos: [
    { valor: 'client_name', rotulo: 'Cliente' },
    { valor: 'billing_model', rotulo: 'Modelo de cobrança' },
  ],
  embrioes: [
    { valor: 'donor_identifier', rotulo: 'Doadora' },
    { valor: 'bull_name', rotulo: 'Touro' },
    { valor: 'client_name', rotulo: 'Cliente' },
  ],
  prenhez: [
    { valor: 'donor_identifier', rotulo: 'Doadora' },
    { valor: 'bull_name', rotulo: 'Touro' },
    { valor: 'client_name', rotulo: 'Cliente' },
  ],
  saidas: [
    { valor: 'client_name', rotulo: 'Cliente' },
    { valor: 'motivo', rotulo: 'Motivo' },
    { valor: 'destino', rotulo: 'Para quem' },
    { valor: 'bull_name', rotulo: 'Touro' },
  ],
  // `dono` e não `client_name`: na view do sêmen a coluna se chama assim, e
  // ela é nula quando a palheta é do próprio laboratório — que é resposta, não
  // buraco. O painel traduz o nulo para "Do laboratório".
  semen: [
    { valor: 'dono', rotulo: 'Dono' },
    { valor: 'touro', rotulo: 'Touro' },
    { valor: 'fornecedor', rotulo: 'Fornecedor' },
  ],
  // O financeiro não agrupa: ele responde totais do laboratório, e a quebra que
  // interessa — por cliente — já é a lista de quem deve, na aba Financeiro.
  financeiro: [],
};

/**
 * Que filtros cada aba comporta.
 *
 * Nem toda aba tem os dois campos: `vw_servicos_resumo` não traz touro (um
 * serviço tem vários), e a saída de estoque não traz o projeto SEBRAE. Mostrar
 * um filtro que o conjunto não sabe responder produz "nenhum resultado" sem
 * explicação — pior que não oferecer.
 */
const FILTROS_POR_ABA = {
  servicos: { sebrae: true,  touro: false, periodo: true  },
  embrioes: { sebrae: true,  touro: true,  periodo: true  },
  prenhez:  { sebrae: true,  touro: true,  periodo: true  },
  saidas:   { sebrae: false, touro: true,  periodo: true  },
  // O sêmen não tem período, e é a única aba assim. Saldo é um RETRATO: ou a
  // palheta está no botijão hoje, ou não está. "Doses entre março e junho" não
  // é pergunta com resposta, e um recorte por data devolveria um número que
  // parece saldo e não é o de ninguém.
  //
  // O filtro de touro também sai: nesta aba o touro já é uma das dimensões de
  // agrupamento, e a tabela é curta o bastante para se achar nela.
  semen:    { sebrae: false, touro: false, periodo: false },
  // Período sim, e é o mesmo dos outros: competência do trabalho fechado. Os
  // números de HOJE dentro do painel não dependem dele, e o painel diz isso.
  financeiro: { sebrae: false, touro: false, periodo: true },
};

const RelatoriosTab = ({
  currentLab, clients = [],
  // Do FIVSystem, para o painel financeiro: os serviços em memória e a regra de
  // valor. Não vêm de view própria porque `calculateServiceValue` mora no
  // cliente, e duplicá-la em SQL criaria uma segunda definição de preço.
  services = [], calculateServiceValue,
  podeVerDinheiro = false,
}) => {
  const [aba, setAba] = useState('servicos');
  const [dados, setDados] = useState({
    servicos: [], embrioes: [], prenhez: [], saidas: [], semen: [], receptoras: [],
  });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [dimensao, setDimensao] = useState('client_name');
  const [sebraeProjeto, setSebraeProjeto] = useState('');
  const [touro, setTouro] = useState('');
  const [granularidade, setGranularidade] = useState('mes');
  const [filtros, setFiltros] = useState({ de: mesesAtras(6), ate: hoje(), clientId: '' });

  const carregar = useCallback(async () => {
    if (!currentLab?.id) return;
    setCarregando(true);
    setErro(null);

    const params = {
      laboratoryId: currentLab.id,
      de: filtros.de || undefined,
      ate: filtros.ate || undefined,
      clientId: filtros.clientId || undefined,
    };

    try {
      // As três de uma vez: trocar de aba não deve esperar rede de novo, e o
      // volume aqui é de centenas de linhas, não de milhares.
      const [servicos, embrioes, prenhez, saidas, semen, receptoras] = await Promise.all([
        listarServicos(params),
        listarProducaoPorDoadora(params),
        listarPrenhez(params),
        // A saída tem data própria (`saida_em`), então o filtro de período
        // dela é outro — por isso não entra em `params`.
        listarSaidas(currentLab.id, { de: filtros.de || undefined, ate: filtros.ate || undefined }),
        // Sem período: o saldo de sêmen é de hoje. Só o cliente recorta.
        listarSemenDoRelatorio({
          laboratoryId: currentLab.id, clientId: filtros.clientId || undefined,
        }),
        // A receptora entra na TE, e o período dela é o da sessão de
        // transferência — o mesmo recorte, outra data.
        listarReceptoras(params),
      ]);
      setDados({
        servicos: servicos || [], embrioes: embrioes || [],
        prenhez: prenhez || [], saidas: saidas || [], semen: semen || [],
        receptoras: receptoras || [],
      });
    } catch (e) {
      console.error('Erro ao carregar relatórios:', e);
      // Sem cópia local de propósito: relatório é para decidir, e número velho
      // apresentado como atual leva a decidir errado.
      setErro(e.message);
      setDados({ servicos: [], embrioes: [], prenhez: [], saidas: [], semen: [], receptoras: [] });
    } finally {
      setCarregando(false);
    }
  }, [currentLab?.id, filtros.de, filtros.ate, filtros.clientId]);

  useEffect(() => { carregar(); }, [carregar]);

  const trocarAba = (nova) => {
    setAba(nova);
    // A dimensão anterior pode não existir na aba nova; cair na primeira dela
    // evita um agrupamento vazio sem explicação.
    const permitidas = (DIMENSOES[nova] || []).map(d => d.valor);
    // Aba sem dimensão nenhuma não mexe na escolha: voltar dela deve encontrar
    // o agrupamento de antes, e não o primeiro da lista.
    if (permitidas.length > 0 && !permitidas.includes(dimensao)) setDimensao(permitidas[0]);
  };

  // O que o PDF precisa dizer no cabeçalho. Nomes legíveis, não ids: quem
  // recebe a folha não tem como resolver um uuid.
  const contexto = {
    laboratorio: currentLab?.name,
    de: filtros.de,
    ate: filtros.ate,
    // A aba sem período diz isso ao cabeçalho do PDF, em vez de mandar datas
    // nulas: "sem data" e "sem dimensão de tempo" se pareceriam, e o papel
    // imprimiria "Período: — a —" sobre um saldo de hoje.
    periodo: !!FILTROS_POR_ABA[aba]?.periodo,
    cliente: clients.find(c => c.id === filtros.clientId)?.name || '',
    sebraeProjeto: FILTROS_POR_ABA[aba]?.sebrae ? sebraeProjeto : '',
    touro: FILTROS_POR_ABA[aba]?.touro ? touro : '',
    agrupamento: (DIMENSOES[aba] || []).find(d => d.valor === dimensao)?.rotulo || '',
  };

  const campo = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {/* Some na aba de sêmen, em vez de ficar lá sem efeito: campo de
              filtro que a tela ignora é promessa que ela não cumpre. */}
          {FILTROS_POR_ABA[aba]?.periodo && (
            <>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">De</label>
                <input type="date" className={campo} value={filtros.de}
                  onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">Até</label>
                <input type="date" className={campo} value={filtros.ate}
                  onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Cliente</label>
            <select className={campo} value={filtros.clientId}
              onChange={(e) => setFiltros({ ...filtros, clientId: e.target.value })}>
              <option value="">Todos</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {FILTROS_POR_ABA[aba]?.sebrae && (
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Projeto SEBRAE</label>
              <input type="text" className={campo} placeholder="Código" value={sebraeProjeto}
                onChange={(e) => setSebraeProjeto(e.target.value)} />
            </div>
          )}
          {FILTROS_POR_ABA[aba]?.touro && (
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Touro</label>
              <input type="text" className={campo} placeholder="Nome do touro" value={touro}
                onChange={(e) => setTouro(e.target.value)} />
            </div>
          )}
          {(DIMENSOES[aba] || []).length > 0 && (
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Agrupar por</label>
              <select className={campo} value={dimensao}
                onChange={(e) => setDimensao(e.target.value)}>
                {DIMENSOES[aba].map(d => (
                  <option key={d.valor} value={d.valor}>{d.rotulo}</option>
                ))}
              </select>
            </div>
          )}
          {FILTROS_POR_ABA[aba]?.periodo && (DIMENSOES[aba] || []).length > 0 && (
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Período</label>
            <select className={campo} value={granularidade}
              onChange={(e) => setGranularidade(e.target.value)}>
              <option value="mes">Por mês</option>
              <option value="dia">Por dia</option>
            </select>
          </div>
          )}
        </div>
      </div>

      {/* Abas do relatório, não do app: ficam abaixo dos filtros porque o
          filtro vale para todas. */}
      {/* ROLA NA HORIZONTAL, como a barra de abas do app.
          Sem `overflow-x-auto`, as seis abas não cabiam na tela do celular:
          em vez de rolarem, elas empurravam a largura da página inteira, e
          todo o conteúdo abaixo saía torto junto. */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {ABAS.filter(a => !a.restrito || podeVerDinheiro).map(a => (
          <button
            key={a.id}
            onClick={() => trocarAba(a.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap
                         transition-colors ${
              aba === a.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      {erro && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
          Não foi possível carregar os relatórios: {erro}
        </div>
      )}

      {carregando && (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-sm text-slate-500">
          Carregando...
        </div>
      )}

      {aba === 'financeiro' && podeVerDinheiro && (
        <PainelFinanceiro
          laboratoryId={currentLab?.id}
          services={services}
          calculateServiceValue={calculateServiceValue}
          de={filtros.de}
          ate={filtros.ate}
        />
      )}

      {!carregando && !erro && aba !== 'financeiro' && (
        <>
          {aba === 'servicos' && (
            <PainelServicos
              contexto={contexto}
              linhas={aplicarFiltrosLocais(dados.servicos, { sebraeProjeto, touro })}
              /* Doadora e receptora vivem nos outros dois conjuntos, porque é lá
                 que o animal existe. Já estão carregados: o relatório busca os
                 cinco de uma vez. */
              producao={aplicarFiltrosLocais(dados.embrioes, { sebraeProjeto, touro })}
              receptoras={aplicarFiltrosLocais(dados.receptoras, { sebraeProjeto, touro })}
              prenhez={aplicarFiltrosLocais(dados.prenhez, { sebraeProjeto, touro })}
              dimensao={dimensao} granularidade={granularidade} />
          )}
          {aba === 'embrioes' && (
            <PainelEmbrioes contexto={contexto} linhas={aplicarFiltrosLocais(dados.embrioes, { sebraeProjeto, touro })} dimensao={dimensao} granularidade={granularidade} />
          )}
          {aba === 'prenhez' && (
            <PainelPrenhez contexto={contexto} linhas={aplicarFiltrosLocais(dados.prenhez, { sebraeProjeto, touro })} dimensao={dimensao} granularidade={granularidade} />
          )}
          {aba === 'saidas' && (
            <PainelSaidas contexto={contexto} linhas={aplicarFiltrosLocais(dados.saidas, { sebraeProjeto, touro })} dimensao={dimensao} granularidade={granularidade} />
          )}
          {/* Sem `aplicarFiltrosLocais`: os filtros que ela aplica (SEBRAE e
              touro) não existem nesta aba, e o cliente já foi recortado no
              banco. */}
          {aba === 'semen' && (
            <PainelSemen contexto={contexto} linhas={dados.semen} dimensao={dimensao} />
          )}
        </>
      )}

      {!carregando && !erro && aba !== 'financeiro' && dados.servicos.length === 0
        && dados.embrioes.length === 0 && dados.prenhez.length === 0 && (
        <div className="text-center text-slate-400 text-xs pt-2">
          <BarChart3 className="mx-auto mb-2 text-slate-300" size={32} />
          Nenhum dado no período — experimente ampliar as datas.
        </div>
      )}
    </div>
  );
};

export default RelatoriosTab;
